import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { cancelNotice, deleteNotice, duplicateNotice, getNotice, listNotices, resendNotice } from "../noticesApi";
import { STATUS_META, TYPE_ICON, describeAudience, formatWhen } from "./noticeHelpers";

const POLL_MS = 4000;

const RECIPIENT_STATUS = {
  sent: { label: "Sent", className: "ok" },
  failed: { label: "Failed", className: "bad" },
  no_email: { label: "No email", className: "warn" },
  pending: { label: "Pending", className: "muted" },
};

const counts = (n) => {
  const s = n.summary || {};
  const parts = [];
  if (n.status === "draft") return "Not sent yet";
  if (n.status === "cancelled") return "Cancelled";
  if (n.status === "scheduled") parts.push(`${s.total || 0} to send`);
  else {
    parts.push(`${s.sent || 0} sent`);
    if (s.failed) parts.push(`${s.failed} failed`);
    if (s.no_email) parts.push(`${s.no_email} no email`);
    if (s.opened) parts.push(`${s.opened} opened`);
    if (n.options?.sms && s.sms_sent) parts.push(`${s.sms_sent} texted`);
  }
  return parts.join(" · ");
};

const whereText = (n) => (n.scope === "company" ? (n.property_ids?.length ? `${n.property_ids.length} properties` : "Whole company") : [n.property_name, n.building_name].filter(Boolean).join(" · "));

/**
 * Every notice for this building (plus company-wide ones), newest first, with the counts that
 * matter and the actions a sent, scheduled or draft notice allows. A notice still sending is
 * polled until it settles.
 */
const NoticeHistory = ({ filterData, scopeFilter, refreshKey, onContinueDraft, onDuplicated }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = scopeFilter === "company" ? { scope: "company" } : { property_id: filterData.property_id, building_name: filterData.building_name || "" };
      const res = await listNotices(params);
      setItems(res?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not load notices");
    } finally {
      setLoading(false);
    }
  }, [filterData.property_id, filterData.building_name, scopeFilter]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // While something is still going out, keep the counts moving.
  useEffect(() => {
    if (!items.some((n) => n.status === "sending")) return undefined;
    const t = setInterval(() => {
      load();
      if (openId) getNotice(openId).then((r) => setDetail(r?.data || null)).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [items, load, openId]);

  const toggle = async (id) => {
    if (openId === id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    try {
      const res = await getNotice(id);
      setDetail(res?.data || null);
    } catch (e) {
      toast.error("Could not load the recipients");
    }
  };

  const run = async (id, fn, successMessage) => {
    setBusyId(id);
    try {
      const res = await fn();
      toast.success(res?.message || successMessage);
      await load();
      if (openId === id) getNotice(id).then((r) => setDetail(r?.data || null)).catch(() => {});
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "That did not work");
      return null;
    } finally {
      setBusyId(null);
    }
  };

  const handleResend = (n) => run(n._id, () => resendNotice(n._id, "missed"), "Resending");
  const handleCancel = async (n) => {
    const ok = await Swal.fire({ title: "Cancel this schedule?", text: "The notice goes back to being a draft; nothing is sent.", icon: "question", showCancelButton: true, confirmButtonText: "Cancel schedule" });
    if (ok.isConfirmed) run(n._id, () => cancelNotice(n._id), "Schedule cancelled");
  };
  const handleDelete = async (n) => {
    const ok = await Swal.fire({ title: "Delete this draft?", icon: "warning", showCancelButton: true, confirmButtonText: "Delete", confirmButtonColor: "#a32d2d" });
    if (ok.isConfirmed) run(n._id, () => deleteNotice(n._id), "Draft deleted");
  };
  const handleDuplicate = async (n) => {
    const res = await run(n._id, () => duplicateNotice(n._id), "Draft created");
    if (res?.data && onDuplicated) onDuplicated(res.data);
  };

  if (loading && items.length === 0) {
    return (
      <div className="rn-empty">
        <Spinner size="sm" /> Loading notices…
      </div>
    );
  }
  if (items.length === 0) {
    return <div className="rn-empty">No notices yet for {scopeFilter === "company" ? "the company" : "this building"}. The first one you send will be listed here with who got it.</div>;
  }

  return (
    <div className="rn-history">
      {items.map((n) => {
        const meta = STATUS_META[n.status] || STATUS_META.draft;
        const open = openId === n._id;
        const missed = (n.summary?.failed || 0) + (n.summary?.no_email || 0);
        const busy = busyId === n._id;
        return (
          <div key={n._id} className={`rn-hist${open ? " is-open" : ""}`}>
            <button type="button" className="rn-hist-head" onClick={() => toggle(n._id)} aria-expanded={open}>
              <span className="rn-hist-ic">
                <FeatherIcon icon={TYPE_ICON[n.type] || "bell"} size={15} />
              </span>
              <span className="rn-hist-main">
                <span className="rn-hist-title">
                  {n.title || <em>Untitled</em>}
                  <span className={`rn-status ${meta.className}`}>{n.status === "sending" ? <Spinner size="sm" /> : null}{meta.label}</span>
                </span>
                <span className="rn-hist-sub">
                  {n.type} · {whereText(n)} · {describeAudience(n.audience)}
                  {n.audience?.include_occupants ? " + occupants" : ""}
                </span>
              </span>
              <span className="rn-hist-side">
                <span className="rn-hist-counts">{counts(n)}</span>
                <span className="rn-hist-when">
                  {n.status === "scheduled" ? `Goes out ${formatWhen(n.options?.scheduled_at)}` : n.status === "draft" ? `Edited ${formatWhen(n.updatedAt)}` : `Sent ${formatWhen(n.sent_at || n.updatedAt)}`}
                  {n.sent_by?.name ? ` · ${n.sent_by.name}` : n.created_by?.name ? ` · ${n.created_by.name}` : ""}
                </span>
              </span>
              <FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={15} className="rn-hist-chev" />
            </button>

            {open && (
              <div className="rn-hist-body">
                <div className="rn-hist-actions">
                  {n.status === "draft" && (
                    <>
                      <button type="button" className="rn-btn rn-btn-primary" onClick={() => onContinueDraft(n)} disabled={busy}>
                        <FeatherIcon icon="edit-2" size={13} /> Continue draft
                      </button>
                      <button type="button" className="rn-btn rn-btn-danger" onClick={() => handleDelete(n)} disabled={busy}>
                        Delete
                      </button>
                    </>
                  )}
                  {n.status === "scheduled" && (
                    <button type="button" className="rn-btn" onClick={() => handleCancel(n)} disabled={busy}>
                      <FeatherIcon icon="x-circle" size={13} /> Cancel schedule
                    </button>
                  )}
                  {n.status === "sent" && missed > 0 && (
                    <button type="button" className="rn-btn rn-btn-primary" onClick={() => handleResend(n)} disabled={busy}>
                      <FeatherIcon icon="refresh-cw" size={13} /> Resend to {missed} missed
                    </button>
                  )}
                  {n.status !== "draft" && (
                    <button type="button" className="rn-btn" onClick={() => handleDuplicate(n)} disabled={busy}>
                      <FeatherIcon icon="copy" size={13} /> Duplicate as new draft
                    </button>
                  )}
                  {busy && <Spinner size="sm" />}
                </div>

                {n.message ? <p className="rn-hist-msg">{n.message}</p> : null}
                {n.attachments?.length ? (
                  <p className="rn-hist-files">
                    <FeatherIcon icon="paperclip" size={12} /> {n.attachments.map((a) => a.filename).join(", ")}
                  </p>
                ) : null}

                {!detail ? (
                  <div className="rn-muted">
                    <Spinner size="sm" /> Loading recipients…
                  </div>
                ) : detail.recipients?.length ? (
                  <div className="rn-recipients">
                    <table>
                      <thead>
                        <tr>
                          <th>Unit</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Opened</th>
                          {detail.options?.sms ? <th>Text</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.recipients.map((r) => {
                          const st = RECIPIENT_STATUS[r.email_status] || RECIPIENT_STATUS.pending;
                          return (
                            <tr key={r.recipient_id}>
                              <td>{r.unit_no}</td>
                              <td>
                                {r.name}
                                {r.role === "occupant" ? <em> occupant</em> : null}
                                {detail.scope === "company" ? <small> {r.property_name}</small> : null}
                              </td>
                              <td>{r.email || <span className="rn-muted">none</span>}</td>
                              <td>
                                <span className={`rn-rstat ${st.className}`} title={r.email_error || ""}>{st.label}</span>
                              </td>
                              <td>{r.opened_at ? formatWhen(r.opened_at) : <span className="rn-muted">no</span>}</td>
                              {detail.options?.sms ? <td>{r.sms_status === "sent" ? "Sent" : r.sms_status === "failed" ? "Failed" : r.sms_status === "no_phone" ? "No number" : ""}</td> : null}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="rn-muted">Recipients are fixed when the notice is sent or scheduled.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export { NoticeHistory };
